import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {withTranslation} from 'react-i18next';
import { TypeAhead } from '../shared/TypeAhead';
import { colors } from '../../shared/styles';
import Search from 'react-icons/lib/md/search';
import { styles } from './styles';
import _ from 'lodash';
import { connect } from 'react-redux';
import {Link} from 'react-router-dom';
import {getResultsUrlForBib} from '../../utils/resultsHelpers';
import {getCourseDetail} from '../../utils/ERPUtil';
import { getEventSummary } from '../../resultsClient/getEventSummary';

class EventResultsSearchComponent extends Component {
  static propTypes = {
    eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ignorePrivacySettings: PropTypes.bool,
    isMobile: PropTypes.bool,
    t: PropTypes.func
  };

  static defaultProps = {
    ignorePrivacySettings: true
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      searchQuery: ''
    };

    this.debouncedFetch = _.debounce(this.props.getEventSummary, 1000);
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.debouncedFetch.cancel();
  }

  render() {
    const {
      ignorePrivacySettings,
      isLoading,
      isMobile,
      results,
      t,
      useMaterialUi,
      eventMetadata: {
        data = {}
      },
      eventId,
      masterEventId
    } = this.props;

    let searchQuery = this.state.searchQuery;
    let flattenedResults = flatten(results).map((result) => { return { ...result, eventId, masterEventId } });

    return (
      <span>
        <TypeAhead
          InputIcon={() =>
            <Search
              size={20}
              color={colors.greyLine}
              style={{verticalAlign: 'bottom'}}
            />}
          inputProps={{
            id:'event-results-search',
            fullWidth: true,
          }}
          useMaterialUi={useMaterialUi !== undefined ? useMaterialUi : true}
          value={searchQuery}
          placeholder={
            isMobile ? t('Search by name or bib') : t('Search by name or bib number')
          }
          dropdownHeight={300}
          dropdownTop={39}
          hideDropdownOverride={!searchQuery}
          options={searchQuery ? flattenedResults : []}
          dropdownStyle={styles.typeaheadDropDown}
          renderOption={(props) => <TypeaheadOption ignorePrivacySettings={ignorePrivacySettings} eventMetadata={data} {...props} />}
          onChange={this.onTyped}
          onOptionSelected={this.onOptionSelected}
          searching={isLoading}
          errorMessage={this.props.errorMessage}
          retrySearch={this.doFetch}
          style={{width: isMobile ? '250px' : '428px'}}
        />
      </span>
    );
  }

  doFetch = (searchTerm = '') => {
    const term = searchTerm || this.state.searchQuery;
    const limit = 20;
    this.debouncedFetch({eventId: this.props.eventId, searchTerm: term, limit});
  };

  //text was typed into input
  onTyped = (e) => {
    //TODO ideally this could be a controlled component to make it more generic, but since
    //it's only used by EventResults for now, just do a specific thing on typing/picking option
    this.setState({searchQuery: e.target.value});
    this.doFetch(e.target.value);
  };

  //clicked on an option or keyed Enter on a focused option
  // As long as the TypeAhead component is creating React Links in the dropdown list, this is an unecessary callback
  onOptionSelected = () => {};
}

function flatten(results) {
  return Object.keys(results || {})
    .reduce((list, id) => list.concat(results[id]), []);
}

/**
 * results - render:
 *  {bib} {name}
 *  {gender} {age} | {courseName}
 */
export const TypeaheadOption = (
  {
    option,
    value,
    isFocused,
    isHovered,
    isSelected,
    ignorePrivacySettings,
    isKiosk = false,
    eventMetadata,
  }) => {

  const {
    age,
    bib,
    entryId,
    eventCourseId,
    eventId,
    gender,
    masterEventId
  } = option;

  const notPrivate = ignorePrivacySettings || option.ShowPersonalData || option.showPersonalData;
  const userLink = getResultsUrlForBib(eventId, eventCourseId, bib, masterEventId, entryId);
  const eventCourseDetail = getCourseDetail(eventCourseId, eventMetadata);
  const { eventCourseName } = eventCourseDetail;

  if(!!_.size(option) && notPrivate) {
    return (
      <Link to={userLink}>
        <div
          className='row mx-0 dropdown-option'
          style={styles.typeaheadOptionContainer(isFocused, isHovered, isSelected, isKiosk)}
        >
          <div className='col-12 px-0 suggestion-text'>
            {suggestionText({searchQuery: value, res: option, isFocused})}
          </div>
          <div className='col-12 px-0' style={styles.typeaheadOptionSpanWrap}>
            <span style={styles.typeaheadOptionGender}>
              {`${gender} ${age || ''}`}
            </span>
            <span>
              {eventCourseName}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return null;
};

function suggestionText({searchQuery, res, isFocused}) {
  const text = [res.bib.trim(), ' ', res.displayName.trim()].join('');

  function sliceMatch(str) {
    const idx = text.toLowerCase().indexOf(str);

    if (idx < 0) {
      const len = str.length;
      if (len > 1) {
        const leftMatch = sliceMatch(str.slice(0, len - 1));
        const rightMatch = sliceMatch(str.slice(1));

        if (leftMatch && rightMatch) {
          return leftMatch[1].length >= rightMatch[1].length
            ? leftMatch : rightMatch;
        }
        return leftMatch || rightMatch;
      }
    }
    else {
      return [
        text.slice(0, idx),
        text.slice(idx, idx + str.length),
        text.slice(idx + str.length)
      ];
    }
  }

  const sliced = sliceMatch(searchQuery.toLowerCase());
  const [left, match, right] = sliced || [text, '', ''];

  return (
    <span style={styles.suggestionText(isFocused)}>
      <span>
        {left}
      </span>
      <span style={{fontWeight:'bold'}}>
        {match}
      </span>
      <span>
        {right}
      </span>
    </span>
  );
}

const mapStateToProps = (state) => {
  return {
    errorMessage: state.eventSummarySearch.errorMessage,
    isLoading: state.eventSummarySearch.isLoading,
    results: state.eventSummarySearch.results,
    eventMetadata: state.eventMetadata
  }
};

const mapDispatchToProps = (dispatch) => {
  return {
    getEventSummary: (query) => {
      dispatch(getEventSummary(query));
    }
  }
};

const EventResultsSearchConnected = connect(
  mapStateToProps,
  mapDispatchToProps
)(EventResultsSearchComponent);

export const EventResultsSearch = withTranslation()(EventResultsSearchConnected);
